import { useUpdateAiAgentServicesSetting } from '@/context/services/ai_agents_setting/AiAgentsSetting.service';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { UpdateAiAgentServiceSettingInputServiceType } from 'ap-openapi';
import { useCallback, useState } from 'react';

type ServiceFormData = {
  invoice_download_agent: boolean;
};

type UseUpdateServicesSettingReturn = {
  updateServicesSetting: (
    data: ServiceFormData,
    successCallback?: () => void
  ) => Promise<void>;
  isUpdating: boolean;
};

export const useUpdateServicesSetting = (): UseUpdateServicesSettingReturn => {
  const [isUpdating, setIsUpdating] = useState(false);
  const notify = useToastAPI();
  const { mutateAsync } = useUpdateAiAgentServicesSetting();
  const { t } = useTranslation();
  const updateServicesSetting = useCallback(
    async (data: ServiceFormData, successCallback?: () => void) => {
      setIsUpdating(true);
      try {
        // Transform form data to API format
        const apiData = [
          {
            service_type:
              UpdateAiAgentServiceSettingInputServiceType.invoice_download_agent,
            is_enabled: data.invoice_download_agent,
          },
        ];
        const resp = await mutateAsync(apiData);
        const isSuccess = resp?.results?.some(
          (result) => result.status === 'success'
        );
        if (isSuccess) {
          notify.success({
            title: t('ai_agents_setting_services_update_success'),
            duration: 5,
          });
          successCallback?.();
        } else {
          notify.error({
            title: t('ai_agents_setting_services_update_error'),
            duration: 5,
          });
        }
      } catch (error) {
        // Show error toast
        notify.error({
          title: t('ai_agents_setting_services_update_error'),
          duration: 5,
        });
        throw error;
      } finally {
        setIsUpdating(false);
      }
    },
    [mutateAsync, notify, t]
  );

  return {
    updateServicesSetting,
    isUpdating,
  };
};
